import { useState } from 'react';
import { useLocation } from 'react-router';
import {
    getPendingHeroImage,
    PROJECT_HERO_TRANSITION_NAME,
    useTileHeroViewTransitionStyle,
} from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';
import { useProjectPreviewRouteData } from '@modules/project/hook/use-project-preview-route-data';
import { DetailHero } from '~/ui-rocco/pages/detail-shared';

/**
 * P2 — project hero. Full-bleed banner photo with the project name overlaid.
 * Maps the project loader into the shared {@link DetailHero} image variant,
 * overriding its geometry to the taller project frame (1920×1373) with the
 * title set mid-lower rather than pinned to the bottom edge. Reads the
 * preview-merged project so BM template-preview drafts show the edited banner.
 *
 * The banner box is the morph counterpart of a listing project tile
 * (`project-hero`); the underlay covers the cold-cache gap with the tile's
 * already-decoded rendition.
 */
export const ProjectHeroSection = () => {
    const project = useProjectPreviewRouteData();
    const { pathname } = useLocation();
    const heroStyle = useTileHeroViewTransitionStyle(
        pathname,
        PROJECT_HERO_TRANSITION_NAME,
    );
    const [underlaySrc] = useState(() => getPendingHeroImage(pathname));

    // Fall back to the social preview image when the banner has no still — e.g.
    // video-only banners — so the hero never disappears.
    const backgroundImage =
        project.Banner?.imageUrl || project.FacebookPreviewImage;
    if (!backgroundImage) return null;

    // A banner set to video in the BM plays over the still. Matching the legacy
    // banner, a stored video URL is enough on its own — some banners carry the
    // URL without the flag having been re-saved.
    const videoBannerUrl = project.Banner?.videoBannerUrl;
    const backgroundVideo =
        project.Banner?.isVideoBanner || videoBannerUrl
            ? videoBannerUrl
            : undefined;

    const professional = project.Professional;

    return (
        <DetailHero
            title={project.ProjectHeroTitle || project.Title}
            backgroundImage={backgroundImage}
            backgroundVideo={backgroundVideo}
            overlay={project.Banner?.overlay}
            aspectClassName="md:aspect-[1200/868] md:max-h-[86.8125rem]"
            heroStyle={heroStyle}
            heroUnderlaySrc={underlaySrc}
            // Fade the hero's bottom edge to the intro section's #181818 so it
            // blends seamlessly into the dark section below. Bottom-snapped and
            // capped at the Figma's 590px (36.875rem) so the fade stays in the
            // lower band rather than washing the whole hero (#181818 fading to
            // transparent by ~70% of that band).
            scrimClassName="absolute inset-x-0 bottom-0 h-[36.875rem] bg-[linear-gradient(to_top,#181818_6.21%,rgba(24,24,24,0)_70.17%)]"
            // Subtle top fade (mobile) so the transparent top nav stays
            // legible over bright banners — light skies otherwise swallow
            // the white logo and icons.
            topScrimClassName="absolute inset-x-0 top-0 h-[7.5rem] bg-[linear-gradient(to_bottom,rgba(0,0,0,0.4),rgba(0,0,0,0))] md:hidden"
            contentClassName="md:top-[52%] md:bottom-auto md:left-[3.564rem] md:max-w-[108rem]"
            titleClassName="text-[#e5e5e5] md:text-[7.5rem] md:leading-[1.08] md:tracking-[-0.228rem]"
            // Desktop: right-align the logo badge with the summary title/CTA
            // group (the section gutter), sized 160×160 (10rem), straddling the
            // hero's bottom edge. Hidden on mobile — the logo already appears in
            // the summary trust row, the About-the-Professional block and the
            // closing band, so a fourth badge over the title is noise.
            logoClassName="max-md:hidden md:right-[2.5rem] md:bottom-0 md:size-[10rem]"
            logo={
                professional?.CompanyLogo
                    ? {
                          src: professional.CompanyLogo,
                          background: professional.LogoBackgroundColor,
                          link: professional.Link,
                      }
                    : undefined
            }
        />
    );
};
